(() => {
  if (window.RokidHalloweenCommerce) { window.RokidHalloweenCommerce.mount(document); return; }
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(pointer: fine)');
  const hoverCapable = matchMedia('(hover: hover)');
  function initJelly(control, abort) {
    let frame = 0;
    let previous = 0;
    let hovered = false;
    let pressed = false;
    let targetX = 0;
    let targetY = 0;
    const values = { x:0, y:0, scale:1, blob:0, label:1 };
    const velocities = { x:0, y:0, scale:0, blob:0, label:0 };
    const disabled = () => control.matches(':disabled,[aria-disabled="true"]');
    const movingAllowed = () => !reduced.matches && !document.hidden;
    const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value));
    const paint = () => {
      control.style.setProperty('--rh-jelly-x', values.x + 'px');
      control.style.setProperty('--rh-jelly-y', values.y + 'px');
      control.style.setProperty('--rh-jelly-body-x', clamp(values.x / 50, 2) + 'px');
      control.style.setProperty('--rh-jelly-body-y', clamp(values.y / 50, 1) + 'px');
      control.style.setProperty('--rh-jelly-label-x', values.x * .05 + 'px');
      control.style.setProperty('--rh-jelly-label-y', values.y * .05 + 'px');
      control.style.setProperty('--rh-jelly-scale', values.scale);
      control.style.setProperty('--rh-jelly-blob-scale', Math.max(0, Math.min(1.08, values.blob)));
      control.style.setProperty('--rh-jelly-label-scale', values.label);
    };
    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      previous = 0;
      control.removeAttribute('data-rh-motion');
    };
    const reset = () => {
      stop();
      hovered = pressed = false;
      targetX = targetY = 0;
      Object.assign(values, {x:0,y:0,scale:1,blob:0,label:1});
      for (const key of Object.keys(velocities)) velocities[key] = 0;
      paint();
    };
    const tick = time => {
      frame = 0;
      if (!control.isConnected || disabled() || !movingAllowed()) { reset(); return; }
      const targets = {x:targetX,y:targetY,scale:pressed ? .94 : hovered ? 1.02 : 1,blob:hovered ? 1 : 0,label:pressed ? .96 : 1};
      const delta = previous ? Math.min((time - previous) / 1000, 1 / 30) : 1 / 60;
      previous = time;
      const steps = Math.max(1, Math.ceil(delta / (1 / 120)));
      const step = delta / steps;
      let settled = true;
      for (const key of Object.keys(values)) {
        for (let i = 0; i < steps; i++) {
          velocities[key] += (200 * (targets[key] - values[key]) - 20 * velocities[key]) * step;
          values[key] += velocities[key] * step;
        }
        const tolerance = key === 'x' || key === 'y' ? .02 : .001;
        if (Math.abs(targets[key] - values[key]) > tolerance || Math.abs(velocities[key]) > tolerance) settled = false;
        else { values[key] = targets[key]; velocities[key] = 0; }
      }
      paint();
      if (!settled) frame = requestAnimationFrame(tick);
      else { previous = 0; control.removeAttribute('data-rh-motion'); }
    };
    const animate = () => {
      if (disabled() || !movingAllowed()) { reset(); return; }
      if (!frame) { control.dataset.rhMotion = 'true'; frame = requestAnimationFrame(tick); }
    };
    const track = event => {
      if (event.pointerType !== 'mouse' || !fine.matches || !hoverCapable.matches || !movingAllowed() || disabled()) return;
      hovered = true;
      const rect = control.getBoundingClientRect();
      targetX = clamp(event.clientX - rect.left - rect.width / 2, Math.max(0, rect.width / 2 - 27));
      targetY = clamp(event.clientY - rect.top - rect.height / 2, Math.max(0, rect.height / 2 - 27));
      animate();
    };
    const release = event => {
      if (!pressed) return;
      pressed = false;
      if (event?.pointerType !== 'mouse') { hovered = false; targetX = targetY = 0; }
      animate();
    };
    const listen = (target, type, handler, passive = true) => target.addEventListener(type, handler, {passive,signal:abort.signal});
    listen(control, 'pointerenter', track);
    listen(control, 'pointermove', track);
    listen(control, 'pointerleave', () => { hovered = pressed = false; targetX = targetY = 0; animate(); });
    listen(control, 'pointerdown', event => {
      if (disabled() || event.button !== 0) return;
      pressed = true;
      if (event.pointerType !== 'mouse') { hovered = false; targetX = targetY = 0; }
      animate();
    });
    listen(window, 'pointerup', release);
    listen(window, 'pointercancel', () => { hovered = pressed = false; targetX = targetY = 0; animate(); });
    listen(control, 'keydown', event => {
      if (disabled() || event.repeat) return;
      if (event.key === 'Enter' || (event.key === ' ' && control.tagName !== 'A')) { pressed = true; animate(); }
    });
    listen(control, 'keyup', release);
    listen(control, 'blur', reset);
    listen(window, 'blur', reset);
    listen(document, 'visibilitychange', () => { if (document.hidden) reset(); });
    for (const query of [reduced,fine,hoverCapable]) listen(query, 'change', reset);
    return {dispose:reset,get active() { return frame !== 0; }};
  }
  function initChooser(root, abort) {
    const section = root.querySelector('[data-rh-chooser]');
    const stage = root.querySelector('[data-rh-choice-stage]');
    const rows = [...root.querySelectorAll('[data-rh-choice]')];
    const controls = [...root.querySelectorAll('[data-rh-choice-select]')];
    const panels = [...root.querySelectorAll('[data-rh-choice-panel]')];
    const labels = [...root.querySelectorAll('[data-rh-choice-static]')];
    if (!section || !stage || !controls.length || rows.length !== controls.length || panels.length !== controls.length || labels.length !== controls.length) return null;
    const keys = controls.map(control => control.dataset.rhChoiceSelect);
    if (new Set(keys).size !== keys.length || keys.some((key,index) => !key || rows[index].dataset.rhChoice !== key || panels[index].dataset.rhChoicePanel !== key)) return null;
    let selected = -1;
    const choose = index => {
      if (selected === index || !root.isConnected || abort.signal.aborted) return;
      selected = index;
      controls.forEach((control,i) => {
        control.setAttribute('aria-pressed', String(i === index));
        panels[i].hidden = i !== index;
        if (i === index) rows[i].dataset.rhChoiceActive = 'true';
        else rows[i].removeAttribute('data-rh-choice-active');
      });
      section.dataset.rhChoiceSelected = keys[index];
    };
    rows.forEach((row,index) => {
      row.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && fine.matches && hoverCapable.matches) choose(index);
      }, {passive:true,signal:abort.signal});
      row.addEventListener('focusin', () => choose(index), {signal:abort.signal});
      controls[index].addEventListener('click', () => choose(index), {signal:abort.signal});
    });
    controls.forEach(control => { control.hidden = false; });
    labels.forEach(label => { label.hidden = true; });
    stage.hidden = false;
    section.dataset.rhPreviewReady = 'true';
    choose(0);
    return {dispose:() => {
      section.removeAttribute('data-rh-preview-ready');
      section.removeAttribute('data-rh-choice-selected');
      stage.hidden = true;
      controls.forEach((control,index) => {
        control.hidden = true;
        control.removeAttribute('aria-pressed');
        panels[index].hidden = true;
        labels[index].hidden = false;
        rows[index].removeAttribute('data-rh-choice-active');
      });
    }};
  }
  function updateLinks() {
    document.querySelectorAll('[data-rh-fallback]').forEach(link => {
      const target = link.dataset.rhTarget || link.getAttribute('href');
      link.dataset.rhTarget = target;
      if (!target?.startsWith('#')) return;
      link.href = document.getElementById(target.slice(1)) ? target : link.dataset.rhFallback;
    });
  }
  function init(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    let frame = 0;
    const jelly = [...root.querySelectorAll('[data-rh-jelly]')].map(control => initJelly(control, abort));
    const chooser = root.dataset.rhCommerce === 'chooser' ? initChooser(root, abort) : null;
    root.querySelectorAll('img').forEach(img => {
      const fail = () => {
        if (img.hidden) return;
        img.hidden = true;
        const label = document.createElement('span');
        label.className = 'rh-image-error';
        label.textContent = root.dataset.imageUnavailable || img.alt;
        img.parentElement.append(label);
      };
      img.addEventListener('error', fail, { signal: abort.signal });
      if (img.complete && !img.naturalWidth) fail();
    });
    const ghost = root.querySelector('[data-rh-ghost]');
    if (ghost) {
      const reset = () => { ghost.style.setProperty('--rh-mx', '0px'); ghost.style.setProperty('--rh-my', '0px'); };
      root.addEventListener('pointermove', event => {
        if (root.dataset.follow !== 'true' || reduced.matches || !fine.matches || window.Shopify?.designMode) return;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const rect = root.getBoundingClientRect();
          const strength = Math.max(0, Math.min(200, Number(root.dataset.strength) || 0)) / 100;
          ghost.style.setProperty('--rh-mx', ((event.clientX - rect.left - rect.width / 2) / 13.5 * strength) + 'px');
          ghost.style.setProperty('--rh-my', ((event.clientY - rect.top - rect.height / 2) / 21 * strength) + 'px');
        });
      }, { passive: true, signal: abort.signal });
      root.addEventListener('pointerleave', reset, { signal: abort.signal });
      reduced.addEventListener('change', reset, { signal: abort.signal });
      fine.addEventListener('change', reset, { signal: abort.signal });
    }
    root.querySelector('[data-rh-dismiss]')?.addEventListener('click', event => {
      event.currentTarget.closest('[data-rh-sticky]').hidden = true;
    }, { signal: abort.signal });
    instances.set(root, {dispose:() => { abort.abort(); cancelAnimationFrame(frame); jelly.forEach(item => item.dispose()); chooser?.dispose(); },jelly});
  }
  function mount(scope) {
    if (scope.matches?.('[data-rh-commerce]')) init(scope);
    scope.querySelectorAll('[data-rh-commerce]').forEach(init);
    updateLinks();
  }
  function unmount(scope) {
    for (const [root, instance] of instances) if (root === scope || scope.contains(root)) { instance.dispose(); instances.delete(root); }
    updateLinks();
  }
  window.RokidHalloweenCommerce = { mount, unmount, get instanceCount() { return instances.size; }, get jellyCount() { return [...instances.values()].reduce((count,instance) => count + instance.jelly.length,0); }, get activeJellyFrameCount() { return [...instances.values()].reduce((count,instance) => count + instance.jelly.filter(item => item.active).length,0); } };
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
})();
